import React, { useEffect, useState } from 'react';
import { Table, message } from 'antd';
import { getReportApiByKey } from '@app/api/reports.api';

interface SummaryReportRow {
  key: string | number;

  department: string;
  total: number;
  grand_total?: number;
  isTypeRow?: boolean;
}

interface SummaryReportProps {
  reportKey: string;
  filters: any;
}

const SummaryReport: React.FC<SummaryReportProps> = ({ reportKey, filters }) => {
  const [data, setData] = useState<SummaryReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [grandTotal, setGrandTotal] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      if (!reportKey || !filters) return;

      const api = getReportApiByKey(reportKey);
      if (!api) {
        message.error('Invalid report key.');
        return;
      }

      setLoading(true);
      try {
        const response = await api.generateReport(filters);

        const departments = response?.report?.departments || {};
        const rows: SummaryReportRow[] = [];

        const grouped: Record<string, { total: number; items: any[] }> = {};

        Object.values(departments).forEach((dept: any) => {
          if (!grouped[dept.type]) {
            grouped[dept.type] = { total: 0, items: [] };
          }
          grouped[dept.type].total += dept.total;
          grouped[dept.type].items.push(dept);
        });

        Object.entries(grouped).forEach(([type, group]) => {
          // TYPE ROW
          rows.push({
            key: `type-${type}`,
            department: type,
            total: group.total,
            isTypeRow: true,
          });

          // DEPARTMENT ROWS
          group.items.forEach((dept: any) => {
            rows.push({
              key: dept.id,
              department: `${dept.name} (${dept.code})`,
              total: dept.total,
            });
          });
        });

        setData(rows);
        setGrandTotal(response?.report?.grand_total ?? 0);
      } catch (err) {
        console.error(err);
        message.error('Failed to fetch Summary Report data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reportKey, filters]);

  const columns: any[] = [
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (value: string, row: SummaryReportRow) => (row.isTypeRow ? <strong>{value}</strong> : value),
    },
    {
      title: 'Total (Rs.)',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (v: number, row: SummaryReportRow) => (
        <strong style={{ fontWeight: row.isTypeRow ? 600 : 400 }}>
          {v.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
        </strong>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      bordered
      pagination={false}
      loading={loading}
      footer={() => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 600,
          }}
        >
          <span>Grand Total</span>
          <span>{grandTotal.toLocaleString('en-LK', { minimumFractionDigits: 2 })}</span>
        </div>
      )}
    />
  );
};

export default SummaryReport;
